import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import ScrollToTop from "./components/ScrollToTop";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import ChatWidget from "./components/ChatWidget";
import ConsentBanner from "./components/ConsentBanner";
import Home from "./pages/Home";
import About from "./pages/About";
import CaseStudies from "./pages/CaseStudies";
import CaseStudyDetail from "./pages/CaseStudyDetail";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Recruiter from "./pages/Recruiter";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import AISolutions from "./pages/AISolutions";
import Speaking from "./pages/Speaking";
import Diagnostic from "./pages/Diagnostic";
import Guestbook from "./pages/Guestbook";
import Community from "./pages/Community";
import BrandKit from "./pages/BrandKit";
import AuthCallback from "./pages/AuthCallback";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminEnquiries from "./pages/admin/AdminEnquiries";
import AdminConversations from "./pages/admin/AdminConversations";
import AdminCaseStudies from "./pages/admin/AdminCaseStudies";
import AdminServices from "./pages/admin/AdminServices";
import AdminExperience from "./pages/admin/AdminExperience";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminAwards from "./pages/admin/AdminAwards";
import AdminDiagnostics from "./pages/admin/AdminDiagnostics";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminEventPhotos from "./pages/admin/AdminEventPhotos";
import AdminSiteContent from "./pages/admin/AdminSiteContent";
import { usePageTracking } from "./hooks/usePageTracking";
import { SiteContentProvider } from "./context/SiteContentContext";
import DraftPreviewBanner from "./components/DraftPreviewBanner";
// Batch 2 §12 — AvailabilityRibbon removed. It relied on a scroll threshold that never
// triggered for most visitors, and the AudienceRouter on Home already covers the
// "For recruiters" pathway.

function PublicChrome({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  usePageTracking();
  return (
    <>
      {!isAdmin && <Nav />}
      {children}
      {!isAdmin && <Footer />}
      {!isAdmin && <ChatWidget />}
    </>
  );
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: synchronous check for session_id in URL hash (before any route renders).
  // Prevents race with AuthProvider's /auth/me check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <PublicChrome>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
        <Route path="/services" element={<Services />} />
        <Route path="/for-recruiters" element={<Recruiter />} />
        {/* Batch 2 §2 — /recruiters was linked from nav labels but had no route
            → blank page. 301 to canonical URL. */}
        <Route path="/recruiters" element={<Navigate to="/for-recruiters" replace />} />
        <Route path="/ai-solutions" element={<AISolutions />} />
        <Route path="/speaking" element={<Speaking />} />
        <Route path="/diagnostic" element={<Diagnostic />} />
        <Route path="/guestbook" element={<Guestbook />} />
        <Route path="/community" element={<Community />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/brand-kit" element={<BrandKit />} />
        <Route path="/privacy" element={<Privacy />} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="enquiries" element={<AdminEnquiries />} />
          <Route path="conversations" element={<AdminConversations />} />
          <Route path="case-studies" element={<AdminCaseStudies />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="experience" element={<AdminExperience />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="awards" element={<AdminAwards />} />
          <Route path="diagnostics" element={<AdminDiagnostics />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="event-photos" element={<AdminEventPhotos />} />
          <Route path="site-content" element={<AdminSiteContent />} />
        </Route>

        {/* Catch-all — must be last. Batch 2 §2. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <ConsentBanner />
    </PublicChrome>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SiteContentProvider>
            <DraftPreviewBanner />
            <AppRouter />
            <Toaster
              theme="dark"
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#151517",
                  color: "#FAF9F6",
                  border: "1px solid #343437",
                  borderRadius: 0,
                },
              }}
            />
          </SiteContentProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
